/**
 * Client-side decryption for data exports.
 *
 * Takes the raw (encrypted, base64-encoded) export JSON from the server
 * and returns a clean, human-readable structure with all fields decrypted
 * and system metadata stripped.
 */

import { decrypt, decryptTitle, getKeyForTeam } from "./encryption";

// ── Raw export types (server response) ──────────────────────────────────────

interface RawEncryptedEntity {
  team_id: string;
  encryption_metadata?: Record<string, string> | null;
  [key: string]: unknown;
}

interface RawExportData {
  metadata: { version: string; exported_at: string };
  teams: Array<RawEncryptedEntity & { team_type: string; name_encrypted?: string | null }>;
  visions: Array<RawEncryptedEntity & { title_encrypted: string; description_encrypted?: string | null }>;
  goals: Array<
    RawEncryptedEntity & {
      title_encrypted: string;
      description_encrypted?: string | null;
      metrics_encrypted?: string | null;
      start_date_encrypted?: string | null;
      end_date_encrypted?: string | null;
      status?: string;
    }
  >;
  activities: Array<
    RawEncryptedEntity & {
      title_encrypted: string;
      description_encrypted?: string | null;
      estimated_minutes_encrypted?: string | null;
      status?: string;
      start_time?: string | null;
    }
  >;
  schedules: Array<
    RawEncryptedEntity & {
      rrule?: string | null;
      schedule_start?: string | null;
      schedule_end?: string | null;
      timezone_created_encrypted?: string | null;
      timezone_latest_encrypted?: string | null;
      is_active?: boolean;
    }
  >;
}

// ── Clean export types (decrypted output) ───────────────────────────────────

interface CleanExportData {
  metadata: { exported_at: string; format_version: string };
  teams: Array<{ name: string | null; type: string }>;
  visions: Array<{ title: string; description: string | null; created_at: string | null; updated_at: string | null }>;
  goals: Array<{
    title: string;
    description: string | null;
    metrics: string | null;
    start_date: string | null;
    end_date: string | null;
    status: string | null;
    created_at: string | null;
    updated_at: string | null;
  }>;
  activities: Array<{
    title: string;
    description: string | null;
    status: string | null;
    estimated_minutes: string | null;
    start_time: string | null;
    created_at: string | null;
    updated_at: string | null;
  }>;
  schedules: Array<{
    recurrence_rule: string | null;
    start: string | null;
    end: string | null;
    timezone: string | null;
    active: boolean;
    created_at: string | null;
    updated_at: string | null;
  }>;
}

const DECRYPT_FAILED = "[Could not decrypt]";

// ── Helpers ─────────────────────────────────────────────────────────────────

async function tryDecryptField(
  encrypted: string | null | undefined,
  ivKey: string,
  metadata: Record<string, string> | null | undefined,
  key: unknown,
): Promise<string | null> {
  if (!encrypted || !metadata?.[ivKey]) return null;
  try {
    return await decrypt(encrypted, { iv: metadata[ivKey] }, key);
  } catch {
    return DECRYPT_FAILED;
  }
}

async function tryDecryptTitle(
  encrypted: string | null | undefined,
  metadata: Record<string, string> | null | undefined,
  key: unknown,
): Promise<string> {
  if (!encrypted || !metadata?.title_iv) return DECRYPT_FAILED;
  try {
    return await decryptTitle(encrypted, metadata.title_iv, key);
  } catch {
    return DECRYPT_FAILED;
  }
}

/**
 * Pre-fetch all unique team keys so each key is resolved once.
 */
async function buildKeyCache(
  entities: RawEncryptedEntity[],
): Promise<Map<string, unknown>> {
  const teamIds = new Set<string>();
  for (const e of entities) {
    if (e.team_id) teamIds.add(e.team_id);
  }

  const cache = new Map<string, unknown>();
  for (const id of teamIds) {
    try {
      cache.set(id, await getKeyForTeam(id));
    } catch {
      // Key unavailable — entities for this team will show decrypt-failure placeholders
    }
  }
  return cache;
}

// ── Main export decryption ──────────────────────────────────────────────────

export async function decryptExportData(raw: RawExportData): Promise<CleanExportData> {
  // Collect all entities to pre-fetch keys
  const allEntities: RawEncryptedEntity[] = [
    ...(raw.teams ?? []),
    ...(raw.visions ?? []),
    ...(raw.goals ?? []),
    ...(raw.activities ?? []),
    ...(raw.schedules ?? []),
  ];
  const keyCache = await buildKeyCache(allEntities);

  const getKey = (teamId: string) => keyCache.get(teamId);

  // Decrypt teams
  const teams = await Promise.all(
    (raw.teams ?? []).map(async (t) => {
      const key = getKey(t.team_id);
      let name: string | null = null;
      if (t.name_encrypted && key) {
        name = await tryDecryptField(t.name_encrypted, "title_iv", t.encryption_metadata, key);
        // Some teams store name IV under "iv" instead of "title_iv"
        if (name === null) {
          name = await tryDecryptField(t.name_encrypted, "iv", t.encryption_metadata, key);
        }
      }
      return { name, type: t.team_type };
    }),
  );

  // Decrypt visions
  const visions = await Promise.all(
    (raw.visions ?? []).map(async (v) => {
      const key = getKey(v.team_id);
      return {
        title: key ? await tryDecryptTitle(v.title_encrypted, v.encryption_metadata, key) : DECRYPT_FAILED,
        description: key ? await tryDecryptField(v.description_encrypted, "iv", v.encryption_metadata, key) : null,
        created_at: (v.created_at as string) ?? null,
        updated_at: (v.updated_at as string) ?? null,
      };
    }),
  );

  // Decrypt goals
  const goals = await Promise.all(
    (raw.goals ?? []).map(async (g) => {
      const key = getKey(g.team_id);
      return {
        title: key ? await tryDecryptTitle(g.title_encrypted, g.encryption_metadata, key) : DECRYPT_FAILED,
        description: key ? await tryDecryptField(g.description_encrypted, "iv", g.encryption_metadata, key) : null,
        metrics: key ? await tryDecryptField(g.metrics_encrypted, "metrics_iv", g.encryption_metadata, key) : null,
        start_date: key ? await tryDecryptField(g.start_date_encrypted, "start_date_iv", g.encryption_metadata, key) : null,
        end_date: key ? await tryDecryptField(g.end_date_encrypted, "end_date_iv", g.encryption_metadata, key) : null,
        status: (g.status as string) ?? null,
        created_at: (g.created_at as string) ?? null,
        updated_at: (g.updated_at as string) ?? null,
      };
    }),
  );

  // Decrypt activities
  const activities = await Promise.all(
    (raw.activities ?? []).map(async (a) => {
      const key = getKey(a.team_id);
      return {
        title: key ? await tryDecryptTitle(a.title_encrypted, a.encryption_metadata, key) : DECRYPT_FAILED,
        description: key ? await tryDecryptField(a.description_encrypted, "iv", a.encryption_metadata, key) : null,
        status: (a.status as string) ?? null,
        estimated_minutes: key
          ? await tryDecryptField(a.estimated_minutes_encrypted, "estimated_minutes_iv", a.encryption_metadata, key)
          : null,
        start_time: (a.start_time as string) ?? null,
        created_at: (a.created_at as string) ?? null,
        updated_at: (a.updated_at as string) ?? null,
      };
    }),
  );

  // Decrypt schedules
  const schedules = await Promise.all(
    (raw.schedules ?? []).map(async (s) => {
      const key = getKey(s.team_id);
      const timezone =
        key
          ? (await tryDecryptField(s.timezone_latest_encrypted, "timezone_latest_iv", s.encryption_metadata, key)) ??
            (await tryDecryptField(s.timezone_created_encrypted, "timezone_created_iv", s.encryption_metadata, key))
          : null;
      return {
        recurrence_rule: (s.rrule as string) ?? null,
        start: (s.schedule_start as string) ?? null,
        end: (s.schedule_end as string) ?? null,
        timezone,
        active: (s.is_active as boolean) ?? true,
        created_at: (s.created_at as string) ?? null,
        updated_at: (s.updated_at as string) ?? null,
      };
    }),
  );

  return {
    metadata: {
      exported_at: raw.metadata.exported_at,
      format_version: raw.metadata.version,
    },
    teams,
    visions,
    goals,
    activities,
    schedules,
  };
}
